import { AxiosError, AxiosResponse } from 'axios';
import {
  IRecoveryPassword,
  IRequestRecoveryPassword,
  ISignIn,
  ISignInResponse,
} from 'types/Auth';
import { Api } from './api/axios.service';

interface IRecover {
  password: string;
  token: string;
}

/**
 * User login by role
 *
 * @param {ISignIn} {
 *   cpf,
 *   email,
 *   password,
 *   role,
 * }
 * @return {*}  {Promise<ISignInResponse>}
 */
const login = async ({
  cpf,
  password,
  role,
  email,
}: ISignIn): Promise<ISignInResponse> => {
  try {
    const { data } = await Api.post<ISignInResponse>(
      role === 'company' ? '/company/login' : '/login',
      { cpf, email, password },
      { params: { role } }
    );

    const timestamp = new Date().getTime() + data.expireAt - 10 * 60 * 1000;

    localStorage.setItem('user', JSON.stringify(data));
    localStorage.setItem('role', role);
    localStorage.setItem('sessionExpireAt', timestamp.toString());

    return data;
  } catch (reason) {
    const { response } = reason as AxiosError<{ error: string }>;

    throw new Error(
      response?.data?.error || 'Não foi possível realizar o login'
    );
  }
};

/**
 * Request a recovery password token
 *
 * @param {IRequestRecoveryPassword} {
 *   cpf,
 *   role,
 *   email,
 * }
 * @return {*}  {Promise<any>}
 */
const requestRecoveryPasswordToken = async ({
  cpf,
  email,
  role,
}: IRequestRecoveryPassword): Promise<any> => {
  try {
    const { data } = await Api.post<ISignInResponse>(
      '/user/recover',
      { cpf, email },
      { params: { role } }
    );

    return data;
  } catch (reason) {
    const { response } = reason as AxiosError<{ error: string }>;

    throw new Error(
      response?.data?.error ||
        'Não foi possível solicitar a recuperação de senha'
    );
  }
};

/**
 * Recover a password by providing a new password and recovery token
 *
 * @param {IRecoveryPassword} {
 *   password,
 *   passwordConfirmation,
 *   token,
 *   role,
 * }
 * @return {*}  {Promise<any>}
 */
const recoveryPasswordWithToken = async ({
  password,
  passwordConfirmation,
  token,
}: IRecoveryPassword): Promise<any> => {
  try {
    const { data } = await Api.put<ISignInResponse>(
      '/recvToken',
      {
        password,
        passwordConfirmation,
      },
      {
        headers: { Authorization: `Bearer ${token}` },
      }
    );

    return data;
  } catch (reason) {
    const { response } = reason as AxiosError<{ error: string }>;

    throw new Error(
      response?.data?.error || 'Não foi possível redefinir a senha'
    );
  }
};

const signIn = (user: ISignIn, role: string) => {
  return Api.post(`/login?role=${role}`, {
    cpf: user.cpf,
    password: user.password,
  }).then((response) => {
    if (response.data.token) {
      const timestamp =
        new Date().getTime() + response.data.expireAt - 10 * 60 * 1000;

      localStorage.setItem('user', JSON.stringify(response.data));
      localStorage.setItem('role', role);
      localStorage.setItem('sessionExpireAt', timestamp.toString());
    }

    return response.data;
  });
};

const signOut = () => {
  localStorage.clear();
};

const recover = (recover: IRecover) => {
  return Api.put(
    '/recvToken',
    {
      password: recover.password,
    },
    {
      headers: { Authorization: 'Bearer ' + recover.token },
    }
  ).then((response) => response.data);
};

const forgotPassword = (cpf: string, role: string) => {
  return Api.post(`/user/recover?role=${role}`, { cpf });
};

export interface IActivateResponse {
  role?: 'tutor' | 'vet';
  status: boolean;
  error?: string;
}
/**
 * Activate tutor/vet account
 *
 * @param {string} token
 * @return {*}  {(Promise<IActivateResponse>)}
 */
const activate = async (token: string): Promise<IActivateResponse> => {
  try {
    const { data }: AxiosResponse<IActivateResponse> = await Api.get(
      '/user/activate',
      {
        headers: { Authorization: 'Bearer ' + token },
      }
    );

    return { role: data.role, status: true };
  } catch (reason) {
    return {
      status: false,
      error: 'Não foi possível ativar sua conta.',
    };
  }
};

export const AuthService = {
  login,
  requestRecoveryPasswordToken,
  recoveryPasswordWithToken,
  signIn,
  signOut,
  recover,
  activate,
  forgotPassword,
};
